import { FULFILLMENT_TYPE } from '@dehaat/kisan-app-bl/models/Vendor';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, RefObject, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { CartContext } from '@/context/CartProvider';
import { UserContext } from '@/context/UserProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import useIsMobile from '@/hooks/useIsMobile';
import { useProductGroup } from '@/hooks/useProductGroup';
import { Product } from '@/models/Product';
import { selectLatLng } from '@/store/features/locationSlice';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { isArray } from '@/utils/helper';
import ProductCardLoading from './ProductCardLoading';
import ProductListingCard from './ProductListingCard';
interface Props {
  products: Product[];
  loading: boolean;
  cartEmpty: boolean;
  nextLoaderRef?: RefObject<HTMLDivElement>;
  noResults?: ReactNode;
  listType: ProductListTypeEnum;
  hideOutOfStockLabel?: boolean;
  showSeeDetailsCTA?: boolean;
  pageType?: 'search' | 'product';
  isWishlist?: boolean;
  onWishlistChange?: () => void;
}
const ProductResults = ({
  products,
  loading,
  cartEmpty,
  nextLoaderRef,
  noResults,
  listType,
  hideOutOfStockLabel = false,
  showSeeDetailsCTA = false,
  pageType,
  isWishlist = false,
  onWishlistChange
}: Props) => {
  const hasSearchedProducts = isArray(products);
  const freshLoad = products?.length === 0 && loading;
  const latLng = useAppSelector(selectLatLng);
  const {
    t
  } = useTranslation('products');
  const {
    user
  } = useContext(UserContext);
  const {
    currentVendor
  } = useContext(CartContext);
  const {
    isMobile,
    isWindowLoading
  } = useIsMobile();
  const [expandedProductId, setExpandedProductId] = useState<null | string>(null);
  const [loadMoreNode, setLoadMoreNode] = useState<HTMLDivElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const relatedScrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollLeftRef = useRef(0);
  const {
    items: relatedProducts,
    loadNext
  } = useProductGroup({
    product_id: Number(expandedProductId),
    vendor_id: Number(currentVendor?.id),
    page_size: 10 // or whatever you want for related products
  });
  const assignLoadMoreNode = useCallback((node: HTMLDivElement | null) => {
    setLoadMoreNode(node);
  }, []);
  const handleToggleExpand = useCallback((productId: string) => {
    lastScrollLeftRef.current = 0;
    setExpandedProductId(productId);
  }, []);
  const loadingCards = useMemo(() => <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 p-4 ${pageType === 'search' ? 'lg:grid-cols-3' : ''}`}>
        <ProductCardLoading count={isMobile && !isWindowLoading ? 8 : 12} />
      </div>, [isMobile, isWindowLoading, pageType]);
  useEffect(() => {
    if (loadMoreObserverRef.current) {
      loadMoreObserverRef.current.disconnect();
      loadMoreObserverRef.current = null;
    }
    if (!loadMoreNode) return;
    const node = loadMoreNode;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        loadNext();
      });
    }, {
      root: null,
      threshold: 0.9
    });
    observer.observe(node);
    loadMoreObserverRef.current = observer;
    return () => {
      observer.disconnect();
      loadMoreObserverRef.current = null;
    };
  }, [loadMoreNode, loadNext]);
  const getColumnCount = useCallback(() => {
    if (typeof window === 'undefined') return 2;
    const width = window.innerWidth;
    if (width >= 1536) return 6;
    if (width >= 1280) return 5;
    if (width >= 1024) return 4;
    if (width >= 768) return 4;
    if (width >= 640) return 3;
    return 2;
  }, [pageType]);
  const columnCount = useMemo(() => getColumnCount(), [products.length, expandedProductId]);
  const expandedIndex = useMemo(() => {
    return expandedProductId ? products.findIndex(p => p.id === expandedProductId) : -1;
  }, [expandedProductId, products]);
  const expandedRowEndIndex = useMemo(() => {
    if (expandedIndex === -1) return -1;
    return Math.min((Math.floor(expandedIndex / columnCount) + 1) * columnCount - 1, products.length - 1);
  }, [expandedIndex, columnCount, products.length]);
  const renderExpandedRow = useCallback(() => {
    if (!relatedProducts || relatedProducts.length === 0) {
      return null;
    }
    const prefetchIndex = Math.max(relatedProducts.length - 2, 0);
    return <div className="col-span-full w-full">
        <div className="bg-[#FFFEEB] p-3">
          <p className="font-semibold text-black text-base">
            {t('buy_these_products_together')}
          </p>
          <div className="flex gap-3 py-1 overflow-x-auto" ref={relatedScrollRef} onScroll={() => {
          if (relatedScrollRef.current) {
            lastScrollLeftRef.current = relatedScrollRef.current.scrollLeft;
          }
        }}>
            {relatedProducts.map((demoProduct, index) => <div key={`${demoProduct}-${index}`} className="w-[180px] sm:w-[200px] lg:w-[220px] shrink-0" ref={index === prefetchIndex ? assignLoadMoreNode : undefined}>
                <ProductListingCard product={demoProduct} position={index} listType={listType} hideOutOfStockLabel={hideOutOfStockLabel || latLng == null} showSeeDetailsCTA={showSeeDetailsCTA} showEditPrice={currentVendor?.fulfillment_type === FULFILLMENT_TYPE.PICKUP_FROM_STORE && user?.is_variant_price_editable} dcId={currentVendor?.entity_id} isWishlist={isWishlist} onWishlistChange={onWishlistChange} />
              </div>)}
          </div>
        </div>
      </div>;
  }, [expandedProductId, relatedProducts, listType, hideOutOfStockLabel, latLng, showSeeDetailsCTA, currentVendor, user, isWishlist, onWishlistChange, assignLoadMoreNode]);
  useLayoutEffect(() => {
    if (relatedScrollRef.current) {
      relatedScrollRef.current.scrollLeft = lastScrollLeftRef.current;
    }
  }, [relatedProducts.length]);
  const showSearchResults = hasSearchedProducts ? <div className={`w-full ${cartEmpty ? '' : 'pb-16'}`}>
      <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 p-2 ${pageType === 'search' ? 'lg:grid-cols-3' : ''}`}>
        {products.map((product, index) => {
        const shouldInsertExpandedHere = index === expandedRowEndIndex && expandedIndex !== -1;
        return <React.Fragment key={`${product.id}-${index}`}>
              <div>
                <ProductListingCard key={product.id} product={product} position={index} listType={listType} hideOutOfStockLabel={hideOutOfStockLabel || latLng == null} showSeeDetailsCTA={showSeeDetailsCTA} showEditPrice={currentVendor?.fulfillment_type === FULFILLMENT_TYPE.PICKUP_FROM_STORE && user?.is_variant_price_editable} dcId={currentVendor?.entity_id} isWishlist={isWishlist} onWishlistChange={onWishlistChange} onShowSimilar={() => {
              handleToggleExpand(product.id);
            }} />
              </div>

              {shouldInsertExpandedHere && renderExpandedRow()}
            </React.Fragment>;
      })}
      </div>

      {nextLoaderRef ? loading ? loadingCards : <div className="p-2" ref={nextLoaderRef} /> : null}
    </div> : <>{noResults}</>;
  return <>{freshLoad ? loadingCards : showSearchResults}</>;
};
export default ProductResults;